import React, { useContext, useState, useEffect } from "react";
import { Box, Input, Button, Text, Image, Icon } from "@chakra-ui/react";
import axios from "axios";
import { RoomContext } from "../context/RoomContext";
import { Link, useNavigate } from 'react-router-dom';
import { Link as ChakraLink } from "@chakra-ui/react";
import { FaStar } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import API_BASE_URL from "../api";
import { hotelAssets } from "../assets/hotelAssets";
import { HOTEL_ADDRESS, HOTEL_LOCATION_URL, HOTEL_NAME } from "../config/hotel";
import { COUNTRIES, INDIAN_CITIES, fetchLocationByPinCode } from "../utils/locationUtils";

const CustomerDetails = () => {
  const { selectedRoomDetails } = useContext(RoomContext);
  const initialCustomerDetails = {
    firstName: "",
    lastName: "",
    companyName: "",
    phoneNumber: "",
    email: "",
    address: "",
    city: "",
    zipCode: "",
    country: "",
  };
  const [customerDetails, setCustomerDetails] = useState(initialCustomerDetails);
  const [orderId, setOrderId] = useState(null);
  const [tax, setTax] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [isBookingInProgress, setIsBookingInProgress] = useState(false); 
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [confirmedBookingData, setConfirmedBookingData] = useState(null);
  const [pinLookupLoading, setPinLookupLoading] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    console.log("Selected Room Details:", selectedRoomDetails); 
    setTax(selectedRoomDetails.taxes);
    setTotalAmount(selectedRoomDetails.totalAmount);
  }, [selectedRoomDetails]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCustomerDetails((prevState) => ({ ...prevState, [name]: value }));
  };

  const handlePhoneChange = (e) => {
    const digits = e.target.value.replace(/\D/g, "").slice(0, 10);
    setCustomerDetails((prevState) => ({ ...prevState, phoneNumber: digits }));
  };

  const handleZipCodeChange = (e) => {
    const digits = e.target.value.replace(/\D/g, "").slice(0, 6);
    setCustomerDetails((prevState) => ({ ...prevState, zipCode: digits }));
  };

  useEffect(() => {
    if (customerDetails.zipCode.length !== 6) return;

    let cancelled = false;

    const lookupPinCode = async () => {
      setPinLookupLoading(true);
      try {
        const location = await fetchLocationByPinCode(customerDetails.zipCode);
        if (!cancelled && location) {
          setCustomerDetails((prevState) => ({
            ...prevState,
            city: location.city,
            country: location.country,
          }));
        }
      } finally {
        if (!cancelled) setPinLookupLoading(false);
      }
    };

    lookupPinCode();

    return () => {
      cancelled = true;
    };
  }, [customerDetails.zipCode]);

  const requiredCustomerFields = [
    "firstName",
    "lastName",
    "phoneNumber",
    "email",
    "address",
    "city",
    "zipCode",
    "country",
  ];

  const validateCustomerDetails = () => {
    const missingFields = requiredCustomerFields.filter(
      (field) => !customerDetails[field]?.trim()
    );

    if (missingFields.length > 0) {
      alert("Please fill all guest details before booking.");
      return false;
    }

    if (!/^\S+@\S+\.\S+$/.test(customerDetails.email.trim())) {
      alert("Please enter a valid email address.");
      return false;
    }

    if (!/^\d{10}$/.test(customerDetails.phoneNumber)) {
      alert("Please enter a valid 10-digit phone number.");
      return false;
    }

    if (!/^\d{6}$/.test(customerDetails.zipCode)) {
      alert("Please enter a valid 6-digit pin code.");
      return false;
    }

    return true;
  };

  // Function to create an order ID
  const createOrderId = () => {
    const now = new Date();
    const formattedDate = now.toISOString().slice(0, 10).replace(/-/g, '');
    const formattedTime = now.toTimeString().slice(0, 8).replace(/:/g, '');
    const uniqueBookingId = `BK-${formattedDate}${formattedTime}-${Math.floor(Math.random() * 1000)}`;
    setOrderId(uniqueBookingId);
    return uniqueBookingId;
  };

  // Automatically generate order ID on component mount
  useEffect(() => {
    createOrderId();
  }, []);

  // Function for "Pay at Hotel" booking
  const handlePayAtHotel = async () => {
    if (isBookingInProgress) return;  // Prevent duplicate submissions
    if (!validateCustomerDetails()) return;
    setIsBookingInProgress(true);
         
    try {
      const bookingDetails = {
        customerDetails,
        roomId: selectedRoomDetails.roomId,
        checkInDate: selectedRoomDetails.checkInDate,
        checkOutDate: selectedRoomDetails.checkOutDate,
        roomType: selectedRoomDetails.roomType,
        adults: selectedRoomDetails.adults,
        kids: selectedRoomDetails.kids,
        amount: totalAmount,
        payment_method: "Pay at Hotel",
        mealPlan: selectedRoomDetails.mealPlan, 
        mealCost: selectedRoomDetails.mealCost,
      };
  
      const response = await axios.post(`${API_BASE_URL}/bookings`, bookingDetails);
      if (response.status === 201) {
        console.log("Booking successful with Pay at Hotel option:", response.data);
        const invoiceBooking = {
          ...response.data.booking,
          customerName: `${customerDetails.firstName} ${customerDetails.lastName}`,
          phoneNumber: customerDetails.phoneNumber,
          email: customerDetails.email,
          address: `${customerDetails.address}, ${customerDetails.city} ${customerDetails.zipCode}, ${customerDetails.country}`,
        };
        sessionStorage.setItem("lastInvoiceBooking", JSON.stringify(invoiceBooking));
        setConfirmedBookingData(invoiceBooking);
        setShowSuccessModal(true);
      } else {
        console.error("Error creating booking with Pay at Hotel option:", response.data);
        alert("Failed to create booking. Please try again.");
      }
    } catch (error) {
      console.error("Error creating booking with Pay at Hotel option:", error);
      alert(error.response?.data?.error || "An error occurred while booking. Please try again.");
    } finally {
      setIsBookingInProgress(false);
    }
  };

  return (
    <div className="bg-gray-100 min-h-screen" style={{ backgroundColor: "#D5D5D5" }}>
      <div className="container mx-auto py-8 px-4">
        {/* Hotel Information Section */}
      

        <Box
          bg="#0C1938"
          p={4}
          rounded="lg"
          shadow="md"
          mb={8}
          display="flex"
          justifyContent="flex-start" // Left-aligns the content
          alignItems="center" // Centers the content vertically
          width="100%" // Covers the full width of the page
          minHeight="120px" // Adjust this value to increase the height of the box
        >
          <Text fontSize="28px" fontWeight="bold" color="white">
            Review your Booking
          </Text>
        </Box>

        <Box 
          position="relative" 
          zIndex={2} 
          mt={-40} 
          p={4} 
          bg="#F5F5F5" 
          rounded="lg" 
          shadow="md"
        >
          <Text fontSize="20px" fontWeight="bold" mt={4}>  
            {HOTEL_NAME}
            <Box ml={2} display="flex" >
              <Icon as={FaStar} color="blue" boxSize="12px" />
              <Icon as={FaStar} color="blue" boxSize="12px" />
              <Icon as={FaStar} color="blue" boxSize="12px" />
              <Icon as={FaStar} color="gray.400" boxSize="12px" />
              <Icon as={FaStar} color="gray.400" boxSize="12px" />
            </Box>
          </Text>
          
          <Text fontSize={{ base: "xs", md: "md" }} mt={{ base: 1, md: 2 }}>
          
            <ChakraLink 
            
              href={HOTEL_LOCATION_URL} 
              isExternal 
              color="blue.500" 
              
            >
             
              {HOTEL_ADDRESS}
            </ChakraLink>
          </Text>
          <Text fontSize={{ base: "xx-small", md: "sm" }} mt={{ base: 1, md: 2 }} fontWeight="bold" color="gray.500">
            Couple Friendly
          </Text>

          {/* Image positioned at the right corner */}
          <Image 
            src={hotelAssets.customerHotelImage || hotelAssets.logo} 
            alt="Hotel Image" 
            boxSize="80px" 
            rounded="md" 
            position="absolute" 
            top="0%" 
            right="2px" // Adds right margin
            transform="translateY(-50%)" // Centers the image vertically
            zIndex={1} // This ensures the image is on top
          />
        </Box>

        <div className="flex flex-col md:flex-row gap-6 mt-3"> {/* Add top margin */}
          {/* Customer Details Section */}
          <Box bg="white" p={6} rounded="lg" shadow="md" w="full" flexBasis="60%"> {/* Adjusted width */}
            <div>
              <Text fontSize="25px" fontWeight="bold" mb={4}>Guest Details</Text>
              {/* Updated grid layout */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-6">
                <div className="flex flex-col">
                  <label className="font-semibold text-sm mb-1">First Name</label>
                  <Input
                    type="text"
                    name="firstName"
                    value={customerDetails.firstName}
                    onChange={handleInputChange}
                    required
                    className="w-full p-2 border rounded focus:outline-none focus:ring"
                  />
                </div>
                <div className="flex flex-col">
                  <label className="font-semibold text-sm mb-1">Last Name</label>
                  <Input
                    type="text"
                    name="lastName"
                    value={customerDetails.lastName}
                    onChange={handleInputChange}
                    required
                    className="w-full p-2 border rounded focus:outline-none focus:ring"
                  />
                </div>
                
                <div className="flex flex-col">
                  <label className="font-semibold text-sm mb-1">Phone Number</label>
                  <Input
                    type="tel"
                    name="phoneNumber"
                    value={customerDetails.phoneNumber}
                    onChange={handlePhoneChange}
                    inputMode="numeric"
                    maxLength={10}
                    required
                    className="w-full p-2 border rounded focus:outline-none focus:ring"
                  />
                </div>
                <div className="flex flex-col">
                  <label className="font-semibold text-sm mb-1">Email</label>
                  <Input
                    type="email"
                    name="email"
                    value={customerDetails.email}
                    onChange={handleInputChange}
                    required
                    className="w-full p-2 border rounded focus:outline-none focus:ring"
                  />
                </div>
                <div className="flex flex-col sm:col-span-2">
                  <label className="font-semibold text-sm mb-1">Address</label>
                  <Input
                    type="text"
                    name="address"
                    value={customerDetails.address}
                    onChange={handleInputChange}
                    required
                    className="w-full p-2 border rounded focus:outline-none focus:ring"
                  />
                </div>
                <div className="flex flex-col">
                  <label className="font-semibold text-sm mb-1">Pin Code</label>
                  <Input
                    type="text"
                    name="zipCode"
                    value={customerDetails.zipCode}
                    onChange={handleZipCodeChange}
                    inputMode="numeric"
                    maxLength={6}
                    required
                    className="w-full p-2 border rounded focus:outline-none focus:ring"
                  />
                  {pinLookupLoading && (
                    <Text fontSize="xs" color="blue.500" mt={1}>
                      Looking up location...
                    </Text>
                  )}
                </div>
                <div className="flex flex-col">
                  <label className="font-semibold text-sm mb-1">City</label>
                  <Input
                    type="text"
                    name="city"
                    list="city-suggestions"
                    value={customerDetails.city}
                    onChange={handleInputChange}
                    required
                    className="w-full p-2 border rounded focus:outline-none focus:ring"
                  />
                  <datalist id="city-suggestions">
                    {INDIAN_CITIES.map((city) => (
                      <option key={city} value={city} />
                    ))}
                  </datalist>
                </div>
                <div className="flex flex-col">
                  <label className="font-semibold text-sm mb-1">Country</label>
                  <Input
                    type="text"
                    name="country"
                    list="country-suggestions"
                    value={customerDetails.country}
                    onChange={handleInputChange}
                    required
                    className="w-full p-2 border rounded focus:outline-none focus:ring"
                  />
                  <datalist id="country-suggestions">
                    {COUNTRIES.map((country) => (
                      <option key={country} value={country} />
                    ))}
                  </datalist>
                </div>
              </div>
              <div className="flex justify-between items-center mt-4">
                
                <div className="text-m">
                  Need help?
                  <Link to="/contact" className="ml-1 text-blue-500 hover:text-blue-700">
                    Contact us
                  </Link>
                </div>
              </div>
            </div>
          </Box>

          {/* Order Summary Section */}
          <Box bg="white" p={6} rounded="lg" shadow="md" w="full" flexBasis="40%"> {/* Adjusted width */}
            <h2 className="text-2xl font-bold mb-4">Order Summary</h2>
            <div className="space-y-2">
              <div className="flex justify-between">
                <Text fontWeight="bold">Order ID:</Text>
                <Text>{orderId || "N/A"}</Text>
              </div>
              <div className="flex justify-between">
                <Text fontWeight="bold">Check-In Date:</Text>
                <Text>{selectedRoomDetails.checkInDate || "N/A"}</Text>
              </div>
              <div className="flex justify-between">
                <Text fontWeight="bold">Check-Out Date:</Text>
                <Text>{selectedRoomDetails.checkOutDate || "N/A"}</Text>
              </div>
              <div className="flex justify-between">
                <Text fontWeight="bold">Room Type:</Text>
                <Text>{selectedRoomDetails.roomType || "N/A"}</Text>
              </div>
              <div className="flex justify-between">
                <Text fontWeight="bold">Adults:</Text>
                <Text>{selectedRoomDetails.adults || "N/A"}</Text>
              </div>
              <div className="flex justify-between">
                <Text fontWeight="bold">Kids:</Text>
                <Text>{selectedRoomDetails.kids || "N/A"}</Text>
              </div>
              <div className="flex justify-between">
                <Text fontWeight="bold">Meal Plan:</Text>
                <Text>complementory breackfast</Text>
              </div>
              


              <div className="flex justify-between">
                <Text fontWeight="bold">Taxes and Service Fees:</Text>
                <Text>Rs. {tax}</Text>
              </div>
              <div className="flex justify-between">
                <Text fontWeight="bold">Total Amount to be Paid:</Text>
                <Text>Rs. {totalAmount}</Text>
              </div>
            </div>
             <Button
              colorScheme="gray"
              mt={4}
              onClick={handlePayAtHotel}
              isDisabled={isBookingInProgress}
             className="w-full bg-gray-500 text-white py-2 px-4 rounded hover:bg-gray-600"
           >
            {isBookingInProgress ? "Booking..." : "Pay at Hotel"}
            </Button>

          </Box>
        </div>
      </div>

      {/* Success Modal with Animations */}
      <AnimatePresence>
        {showSuccessModal && confirmedBookingData && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/60 backdrop-blur-md"
            />

            {/* Modal Card */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              transition={{ type: "spring", duration: 0.5 }}
              className="relative bg-white rounded-2xl shadow-2xl p-6 md:p-8 max-w-md w-full overflow-hidden text-slate-800 z-10"
            >
              {/* Top gradient indicator */}
              <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-green-400 to-emerald-500" />

              {/* Animated checkmark icon */}
              <div className="flex justify-center mb-6">
                <div className="relative flex items-center justify-center w-20 h-20 bg-green-50 rounded-full">
                  <motion.div
                    initial={{ scale: 0.8, opacity: 0.5 }}
                    animate={{ scale: 1.2, opacity: 0 }}
                    transition={{ duration: 1.5, repeat: Infinity, ease: "easeOut" }}
                    className="absolute inset-0 rounded-full border-2 border-green-400"
                  />
                  <motion.svg
                    className="w-10 h-10 text-green-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={3}
                  >
                    <motion.path
                      initial={{ pathLength: 0 }}
                      animate={{ pathLength: 1 }}
                      transition={{ delay: 0.2, duration: 0.6, ease: "easeInOut" }}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M5 13l4 4L19 7"
                    />
                  </motion.svg>
                </div>
              </div>

              <h3 className="text-2xl font-bold text-center text-slate-900 mb-2">
                Booking Confirmed!
              </h3>
              <p className="text-center text-slate-600 text-sm mb-6">
                Your reservation at <span className="font-semibold text-slate-800">{HOTEL_NAME}</span> has been confirmed.
              </p>

              {/* Invoice-style summary details */}
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 mb-6 text-sm text-left space-y-3">
                <div className="flex justify-between items-center border-b border-slate-200 pb-2">
                  <span className="text-slate-500 font-medium">Booking ID:</span>
                  <span className="font-mono font-bold text-slate-900 bg-slate-200/80 px-2 py-0.5 rounded text-xs">
                    {confirmedBookingData.bookingId}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-4 pt-1">
                  <div>
                    <span className="text-slate-500 block text-xs">Guest Name</span>
                    <span className="font-semibold text-slate-800">
                      {confirmedBookingData.customerName}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-500 block text-xs">Room Type</span>
                    <span className="font-semibold text-slate-800">
                      {confirmedBookingData.roomType}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-slate-500 block text-xs">Check-In</span>
                    <span className="font-semibold text-slate-800">
                      {new Date(confirmedBookingData.checkInDate).toLocaleDateString("en-IN", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-500 block text-xs">Check-Out</span>
                    <span className="font-semibold text-slate-800">
                      {new Date(confirmedBookingData.checkOutDate).toLocaleDateString("en-IN", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })}
                    </span>
                  </div>
                </div>

                <div className="flex justify-between items-center pt-2 border-t border-slate-200">
                  <span className="text-slate-900 font-bold">Total Price:</span>
                  <span className="text-lg font-extrabold text-slate-900">
                    Rs. {confirmedBookingData.amount}
                  </span>
                </div>

                <p className="text-[11px] text-amber-700 font-medium text-center bg-amber-50 rounded-md py-1.5 px-2 border border-amber-200 mt-2">
                  ℹ️ Payment method: Pay at Hotel on arrival.
                </p>
              </div>

              {/* Navigation buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => {
                    navigate("/invoice?flow=customer", { state: { booking: confirmedBookingData, fromCustomerFlow: true } });
                  }}
                  className="flex-1 bg-slate-900 text-white font-semibold py-3 px-4 rounded-xl hover:bg-slate-800 transition shadow-md hover:shadow-lg flex items-center justify-center gap-2"
                >
                  📄 View Invoice
                </button>
                <button
                  onClick={() => {
                    navigate("/");
                  }}
                  className="flex-1 bg-blue-600 text-white font-semibold py-3 px-4 rounded-xl hover:bg-blue-700 transition shadow-md hover:shadow-lg"
                >
                  Go to Home
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CustomerDetails;
